// LAP-733 (GE022) round 11 — translated + redesigned subscription panel.
// All visible strings (plan titles, statuses, button labels, history/invoice
// section headers, toasts, cancellation dialog) now resolve via
// useTranslation('layout'). Visual polish: a status hero card with chips, a
// stacked action bar, plain rows for the history/factures lists, and the
// same sticky footer used by the other settings panes.
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/es'

import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { setSiteUser } from 'reduxLib/slices/userSlice'
import {
  cancelSubscription,
  createPortalSession,
  downgradeSubscription,
  fetchSubscriptionHistory,
  fetchSubscriptionInvoices,
  fetchSubscriptionSummary,
  refreshCurrentUser,
  upgradeSubscription,
} from 'utils/subscription'
// Système de crédits Life — section "Mes crédits" intégrée au panneau Abonnement
import CreditsSection from '../Billing/CreditsSection'

const NAVY = '#001B42'
const ACCENT = '#2563EB'

export default function Abonnement({ handleClose, fetchUser }) {
  const { t, i18n: i18nInstance } = useTranslation('layout')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [userPlan, setUserPlan] = useState({})
  const [logs, setLogs] = useState([])
  const [invoices, setInvoices] = useState([])
  const [cancelSub, setCancelSub] = useState(false)

  // moment.locale follows the active i18n language so the "Prochaine
  // échéance" / "Next billing date" string renders in the right locale.
  useEffect(() => {
    const lang = (i18nInstance.language || 'fr').split('-')[0]
    moment.locale(['fr', 'en', 'de', 'it', 'es'].includes(lang) ? lang : 'fr')
  }, [i18nInstance.language])

  useEffect(() => {
    loadSubscription()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { handleSubmit } = useForm({ defaultValues: {} })

  const loadSubscription = async () => {
    const [summaryResponse, historyResponse, invoiceResponse] = await Promise.all([
      fetchSubscriptionSummary(),
      fetchSubscriptionHistory(),
      fetchSubscriptionInvoices(),
    ])
    if (summaryResponse?.status === 200) setUserPlan(summaryResponse.data || {})
    if (historyResponse?.status === 200) setLogs(historyResponse.data || [])
    if (invoiceResponse?.status === 200) setInvoices(invoiceResponse.data || [])
  }

  const onSubmit = async () => {
    if (fetchUser) fetchUser()
  }

  const handlePortal = async () => {
    const response = await createPortalSession()
    if (response?.status === 200 && response?.data?.url) {
      window.location.href = response.data.url
      return
    }
    toast.error(response?.data?.detail || t('settings.subscription.toasts.portalError'))
  }

  const handlePlanAction = async (targetPlan) => {
    if (!userPlan?.plan) {
      handleClose()
      navigate('/subscription')
      return
    }
    const currentRank = userPlan?.access_plan === 'premium' ? 2 : 1
    const targetRank = targetPlan === 'premium' ? 2 : 1
    const response =
      targetRank > currentRank
        ? await upgradeSubscription(targetPlan)
        : await downgradeSubscription(targetPlan)

    if (response?.status === 200) {
      if (response?.data?.checkout_url) {
        window.location.href = response.data.checkout_url
        return
      }
      await loadSubscription()
      await refreshCurrentUser(dispatch, setSiteUser)
      toast.success(
        targetRank > currentRank
          ? t('settings.subscription.toasts.upgraded')
          : t('settings.subscription.toasts.scheduled'),
      )
      return
    }
    toast.error(response?.data?.detail || t('settings.subscription.toasts.planError'))
  }

  const title = useMemo(() => {
    if (!userPlan?.plan) return t('settings.subscription.noPlan')
    // LAP-739 Bug 1 — when the user is on a Free trial we must NOT display
    // "ABONNEMENT STANDARD" as the active plan. Detect the trialing status
    // (or an explicit free-trial plan name) and surface a dedicated label.
    const rawStatus = String(userPlan?.status || '').toLowerCase()
    const rawPlan = String(userPlan?.plan || '').toLowerCase()
    const isFreeTrial =
      rawStatus === 'trialing' ||
      rawPlan.includes('free trial') ||
      rawPlan.includes('essai') ||
      userPlan?.access_plan === 'free_trial'
    if (isFreeTrial) {
      return t('settings.subscription.freeTrialPlan', { defaultValue: 'Essai gratuit' })
    }
    if (userPlan?.access_plan === 'premium')
      return t('settings.subscription.premiumPlan')
    return t('settings.subscription.standardPlan')
  }, [userPlan, t])

  const translatedStatus = (rawStatus) => {
    if (!rawStatus) return ''
    const key = String(rawStatus).toLowerCase()
    return t(`settings.subscription.statusValues.${key}`, { defaultValue: rawStatus })
  }

  const statusChipColor = (rawStatus) => {
    const s = String(rawStatus || '').toLowerCase()
    if (['active', 'trialing', 'free trial'].includes(s)) return 'success'
    if (['past_due', 'incomplete'].includes(s)) return 'warning'
    if (['canceled'].includes(s)) return 'default'
    return 'default'
  }

  return (
    <>
      {cancelSub && (
        <CancelSub
          setCancelSub={setCancelSub}
          reload={loadSubscription}
          dispatch={dispatch}
          t={t}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Box display="flex" flexDirection="column" height="100%" sx={{ bgcolor: '#F7F9FC' }}>
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
              color: 'white',
              p: isMobile ? 2 : 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('settings.subscription.title')}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{ ml: 'auto', color: 'white' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 4, py: 3 }}>
            {/* Plan hero ------------------------------------------------- */}
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', mb: 3 }}>
              <CardContent>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={2}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  justifyContent="space-between"
                >
                  <Stack spacing={1}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: NAVY, lineHeight: 1.2 }}
                    >
                      {title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {userPlan?.status && (
                        <Chip
                          size="small"
                          label={translatedStatus(userPlan.status)}
                          color={statusChipColor(userPlan.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      )}
                      {userPlan?.cancel_at_period_end && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={t('settings.subscription.cancelAtPeriodEnd')}
                        />
                      )}
                      {userPlan?.scheduled_plan && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('settings.subscription.scheduledChange', {
                            plan: userPlan.scheduled_plan,
                          })}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {userPlan?.next_billing_date
                        ? t('settings.subscription.nextBilling', {
                            date: userPlan.next_billing_date,
                          })
                        : t('settings.subscription.noBilling')}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    /* LAP-739 Bug 3 — go to the dedicated plan-selection page */
                    onClick={() => {
                      if (typeof handleClose === 'function') handleClose()
                      navigate('/choose-plan')
                    }}
                  >
                    {userPlan?.plan
                      ? t('settings.subscription.viewOffers')
                      : t('settings.subscription.subscribe')}
                  </Button>
                  {!!userPlan?.plan && userPlan?.access_plan !== 'premium' && (
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#1D4ED8' },
                      }}
                      onClick={() => handlePlanAction('premium')}
                    >
                      {t('settings.subscription.goPremium')}
                    </Button>
                  )}
                  {!!userPlan?.plan && userPlan?.access_plan === 'premium' && (
                    <Button
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={() => handlePlanAction('standard')}
                    >
                      {t('settings.subscription.backToStandard')}
                    </Button>
                  )}
                  {!!userPlan?.stripe_customer_id && (
                    <Button
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={handlePortal}
                    >
                      {t('settings.subscription.billingPortal')}
                    </Button>
                  )}
                  {!!userPlan?.plan && !userPlan?.cancel_at_period_end && (
                    <Button
                      variant="text"
                      color="error"
                      sx={{ textTransform: 'none' }}
                      onClick={() => setCancelSub(true)}
                    >
                      {t('settings.subscription.cancel')}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Mes crédits Life ------------------------------------------
                Section solde + achat de packs + historique des mouvements.
                Insérée entre le plan hero et l'historique d'activité pour
                rester logiquement groupée avec la gestion d'abonnement. */}
            <CreditsSection />

            {/* History --------------------------------------------------- */}
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', mb: 3 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                  {t('settings.subscription.history')}
                </Typography>
                {logs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.subscription.noHistory')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {logs.map((row) => (
                      <Stack
                        key={row.id}
                        direction={isMobile ? 'column' : 'row'}
                        justifyContent="space-between"
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        spacing={0.5}
                        py={1}
                      >
                        <Typography variant="body2">
                          {row.change_reason || `${row.old_plan} → ${row.new_plan}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {moment(row.change_date).format('LLL')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Invoices -------------------------------------------------- */}
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                  {t('settings.subscription.invoices')}
                </Typography>
                {invoices.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.subscription.noInvoices')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {invoices.map((invoice) => (
                      <Stack
                        key={invoice.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1}
                      >
                        <Chip
                          size="small"
                          label={invoice.status}
                          color={invoice.status === 'paid' ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Button
                          variant="text"
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          href={invoice.hosted_invoice_url || invoice.invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ textTransform: 'none' }}
                        >
                          {t('settings.subscription.open')}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              borderTop: '1px solid #E5E7EB',
              bgcolor: 'white',
              px: isMobile ? 2 : 3,
              py: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={handleClose}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                {t('settings.subscription.close')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#1D4ED8' },
                  px: 3,
                }}
              >
                {t('settings.subscription.save')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </form>
    </>
  )
}

Abonnement.propTypes = {
  handleClose: PropTypes.func.isRequired,
  fetchUser: PropTypes.func,
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function CancelSub({ setCancelSub, reload, dispatch, t }) {
  const [open, setOpen] = useState(true)
  const [reason, setReason] = useState('')
  const [error, setError] = useState(false)

  const closeLocal = () => {
    setOpen(false)
    setCancelSub(false)
  }

  const handleCancelSub = async () => {
    if (!reason.trim()) {
      setError(true)
      return
    }
    const response = await cancelSubscription(reason)
    if (response?.status === 200) {
      await reload()
      await refreshCurrentUser(dispatch, setSiteUser)
      closeLocal()
      toast.success(t('settings.subscription.toasts.cancelScheduled'))
      return
    }
    toast.error(response?.data?.detail || t('settings.subscription.toasts.cancelError'))
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={closeLocal}
      aria-describedby="cancel-sub-description"
    >
      <DialogContent>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>
          {t('settings.subscription.cancelDialog.title')}
        </Typography>
        <TextField
          label={t('settings.subscription.cancelDialog.reasonLabel')}
          sx={{ width: '100%' }}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            setError(false)
          }}
          error={error}
          helperText={error ? t('settings.subscription.cancelDialog.reasonError') : ''}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeLocal} sx={{ textTransform: 'none' }}>
          {t('settings.subscription.cancelDialog.close')}
        </Button>
        <Button
          onClick={handleCancelSub}
          color="error"
          variant="contained"
          sx={{ textTransform: 'none' }}
        >
          {t('settings.subscription.cancelDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
CancelSub.propTypes = {
  setCancelSub: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}
